'use client';

import React from 'react';
import { Modal } from '@/components/ui/Modal';
import { IconCheckCircle, IconAlertCircle } from '@repo/svgs';
import { cn } from '@/lib/cn';

interface BedbugHistoryEntry {
  filingDate: string;
  infestedDwellingUnitCount?: number;
  reInfestedDwellingUnit?: number;
  totalDwellingUnits?: number;
  eradicatedUnitCount?: number;
}

interface BedbugHistoryModalProps {
  open: boolean;
  onClose: () => void;
  bedbugData: BedbugHistoryEntry[];
  isBlurRequired?: boolean;
}

interface BedbugHistoryContentProps {
  bedbugData: BedbugHistoryEntry[];
  isBlurRequired?: boolean;
}

// Format date to "MMM DD, YYYY" format
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
};

// Content component for use with ModalTrigger
export function BedbugHistoryContent({
  bedbugData,
  isBlurRequired = false,
}: BedbugHistoryContentProps) {
  const entries = Array.isArray(bedbugData) ? bedbugData : [];

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-body-2 text-secondary">No bedbug history data available.</p>
      </div>
    );
  }

  return (
    <div>
      {/* History Entries */}
      {entries.map((entry, index, array) => {
        const infestedCount = Number(entry.infestedDwellingUnitCount ?? 0);
        const reinfestedCount = Number(entry.reInfestedDwellingUnit ?? 0);
        const totalUnits = Number(entry.totalDwellingUnits ?? 0);
        const eradicatedCount = Number(entry.eradicatedUnitCount ?? 0);
        const isClear = infestedCount === 0 && reinfestedCount === 0;
        const formattedDate = formatDate(entry.filingDate);

        return (
          <div key={index}>
            <div className="flex items-start gap-4 px-2 pt-3">
              {isClear ? (
                <IconCheckCircle className="w-6 h-6 text-success shrink-0" />
              ) : (
                <IconAlertCircle className="w-6 h-6 text-alert shrink-0" />
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 pb-3">
                  <span className={cn('text-body-2 text-primary')}>
                    {isClear ? 'No bedbugs' : 'Bedbugs detected'}
                  </span>
                  <span className="text-body-2 text-primary">{formattedDate}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-body-2 text-secondary pb-3">
                  <div>
                    <span className="text-body-2-high">{totalUnits}</span> Total units
                  </div>
                  <div>
                    <span className="text-body-2-high">{eradicatedCount}</span> Eradicated units
                  </div>
                  <div>
                    <span className="text-body-2-high">{infestedCount}</span> Infested units
                  </div>
                  <div>
                    <span className="text-body-2-high">{reinfestedCount}</span> Reinfested units
                  </div>
                </div>
              </div>
            </div>
            {index < array.length - 1 && <div className="border-b border-tertiary my-1 mx-2"></div>}
          </div>
        );
      })}

      {/* Regulatory Information */}
      <div className="space-y-2 bg-secondary rounded-2xl p-4 mt-4 mx-2">
        <p className="text-body-2 text-secondary">
          Property owners are required to obtain bedbug infestation history from tenants or dwelling
          unit owners annually. The reporting period is November of the previous year through
          October of the current year.
        </p>
        <p className="text-body-2 text-secondary">
          Property owners are required to file the Annual Bedbug Report between December 1 and
          December 31 of each year. Once December 31 passes, HPD may issue a violation for failure
          to file.
        </p>
      </div>
    </div>
  );
}
